/**
 * useWlAddons — listagem e mutações de add-ons no painel /wl
 *
 * Usa as RPCs `wl_list_addons`, `wl_upsert_addon`, `wl_toggle_addon_active`
 * e `wl_list_addon_providers`. Cache compartilhado via React Query.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { createLogger } from "@/lib/logger";

const log = createLogger("useWlAddons");

export interface WlProviderPrice {
  id?: string;
  provider_id: string;
  price_monthly: number;
  setup_fee: number;
  is_active: boolean;
  // IDs do Stripe por provider (editáveis no painel WL).
  stripe_price_monthly_id?: string | null;
  stripe_setup_price_id?: string | null;
}

/**
 * v1.6.0 (2026-08-18) — Opção de recorrência do add-on (integração V1).
 * Add-ons V1 têm `price_monthly = 0` na raiz: o preço real vive aqui,
 * uma linha por periodicidade (mensal | trimestral | semestral | anual).
 */
export interface WlAddonRecurrenceOption {
  id: string;
  recurrence_interval: string;
  price: number;
  setup_fee: number;
  discount_percent: number | null;
  uaizy_master_service_id: string | null;
  stripe_price_id: string | null;
}




export interface WlAddon {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  addon_type: string;
  task_quota: number | null;
  storage_quota_mb: number | null;
  max_referral_campaigns: number | null;
  max_referral_programs: number | null;
  max_referral_affiliates: number | null;
  referral_bonus_credits: number | null;
  conversion_credit_quota: number | null;
  price_monthly: number;
  price_yearly: number;
  setup_fee: number;
  has_setup_fee: boolean;
  unit_value: number;
  max_quantity: number | null;
  min_plan_required: string | null;
  available_plans: string[];
  sort_order: number;
  // IDs do Stripe (editáveis no painel WL para conectar produto/preço no integrador financeiro).
  stripe_product_id: string | null;
  stripe_price_monthly_id: string | null;
  stripe_price_yearly_id: string | null;
  stripe_setup_price_id: string | null;
  // Multi-gateway v1 (Fase 1/2) — modo de cobrança do add-on. Para combinar com
  // um plano, ambos precisam ter o mesmo billing_integration + billing_gateway_slug.
  billing_integration: "legado" | "integracao_v1";
  billing_gateway_slug: string | null;
  uaizy_master_service_id: string | null;
  // Modo de cobrança do add-on: 'recorrente' (usa plan_addon_recurrence_options)
  // ou 'avulso' (usa plan_addon_one_time_options — compra única).
  billing_mode: "recorrente" | "avulso";
  is_active: boolean;
  created_at: string;
  updated_at: string;
  provider_prices: WlProviderPrice[];
  // Opção avulsa serializada (snapshot da RPC wl_list_addons; pode ser null).
  one_time_option: {
    id: string;
    addon_id: string;
    uaizy_master_service_id: string;
    price: number;
    setup_fee: number;
    discount_percent: number | null;
    is_active: boolean;
  } | null;
  // v1.5.0 (2026-06-28): provider liberado para add-on de instância V1.
  // NULL em qualquer outro tipo/integração.
  provider_id: string | null;
  // v1.6.0 (2026-08-18): preços por periodicidade (add-ons V1).
  recurrence_options: WlAddonRecurrenceOption[];

}


export interface WlAddonFilters {
  includeInactive?: boolean;
  search?: string | null;
  type?: string | null;
}

export function useWlAddons(filters: WlAddonFilters = {}) {
  const includeInactive = filters.includeInactive ?? true;
  const search = (filters.search || "").trim() || null;
  const type = filters.type || null;

  return useQuery({
    queryKey: ["wl-addons", { includeInactive, search, type }],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<WlAddon[]> => {
      const { data, error } = await supabase.rpc("wl_list_addons" as never, {
        p_include_inactive: includeInactive,
        p_search: search,
        p_type: type,
      } as never);
      if (error) {
        log.error("Erro ao listar add-ons", { error });
        throw error;
      }
      return ((data || []) as any[]).map((a) => ({
        ...a,
        billing_mode: (a.billing_mode as "recorrente" | "avulso") || "recorrente",
        price_monthly: Number(a.price_monthly) || 0,
        price_yearly: Number(a.price_yearly) || 0,
        setup_fee: Number(a.setup_fee) || 0,
        available_plans: Array.isArray(a.available_plans) ? a.available_plans : [],
        provider_prices: Array.isArray(a.provider_prices) ? a.provider_prices : [],
        // v1.6.0: normaliza números das opções de recorrência (add-ons V1)
        recurrence_options: Array.isArray(a.recurrence_options)
          ? a.recurrence_options.map((o: any) => ({
              ...o,
              price: Number(o.price) || 0,
              setup_fee: Number(o.setup_fee) || 0,
              discount_percent:
                o.discount_percent !== null && o.discount_percent !== undefined
                  ? Number(o.discount_percent)
                  : null,
            }))
          : [],

        one_time_option: a.one_time_option
          ? {
              ...a.one_time_option,
              price: Number(a.one_time_option.price) || 0,
              setup_fee: Number(a.one_time_option.setup_fee) || 0,
              discount_percent:
                a.one_time_option.discount_percent !== null &&
                a.one_time_option.discount_percent !== undefined
                  ? Number(a.one_time_option.discount_percent)
                  : null,
            }
          : null,
      })) as WlAddon[];
    },
  });
}

export interface WlAddonProvider {
  id: string;
  // Slug intencionalmente removido — sigiloso do root admin.
  name: string;
  display_name: string | null;
}

export function useWlAddonProviders() {
  return useQuery({

    queryKey: ["wl-addon-providers"],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<WlAddonProvider[]> => {
      const { data, error } = await supabase.rpc("wl_list_addon_providers" as never);
      if (error) throw error;
      return (data || []) as WlAddonProvider[];
    },
  });
}

/**
 * v1.0.0 (2026-06-28) — Providers elegíveis para add-on do tipo "Instâncias adicionais"
 * em integracao_v1. Lista apenas providers ativos cujo channel_type pertence a
 * ('whatsapp','whatsapp_oficial'). Usado pelo seletor "Provider liberado".
 */
export interface WlInstanceEligibleProvider extends WlAddonProvider {
  channel_type: string;
}

export function useWlInstanceEligibleProviders(enabled = true) {
  return useQuery({
    queryKey: ["wl-instance-eligible-providers"],
    enabled,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<WlInstanceEligibleProvider[]> => {
      const { data, error } = await supabase.rpc(
        "wl_list_instance_eligible_providers" as never,
      );
      if (error) throw error;
      return (data || []) as WlInstanceEligibleProvider[];
    },
  });
}

export function useWlAddonMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["wl-addons"] });

  const upsert = useMutation({
    mutationFn: async (input: {
      payload: Record<string, unknown>;
      provider_prices?: WlProviderPrice[];
      id?: string | null;
    }) => {
      const { data, error } = await supabase.rpc("wl_upsert_addon" as never, {
        p_payload: input.payload,
        p_provider_prices: input.provider_prices ?? [],
        p_id: input.id ?? null,
      } as never);
      if (error) throw error;
      return data as string;
    },
    onSuccess: invalidate,
  });

  const toggleActive = useMutation({
    mutationFn: async (input: { id: string; active: boolean }) => {
      const { data, error } = await supabase.rpc("wl_toggle_addon_active" as never, {
        p_id: input.id,
        p_active: input.active,
      } as never);
      if (error) throw error;
      return data as boolean;
    },
    onSuccess: invalidate,
  });

  return { upsert, toggleActive };
}
